import customerRideApi from 'api/customerRideApi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactBingmaps } from 'react-bingmaps';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';
import {
  RIDE_LOCATION_MIN_QUERY_LENGTH,
  RIDE_LOCATION_REVERSE_DEBOUNCE_MS,
  RIDE_LOCATION_SEARCH_DEBOUNCE_MS,
} from '../rideLocationSearchPolicy';

const DEFAULT_CENTER = [21.0285, 105.8542];
const mapOptions = {
  liteMode: true,
  showLocateMeButton: false,
  showMapTypeSelector: false,
  showScalebar: false,
  showTermsLink: false,
  showZoomButtons: true,
  showDashboard: false,
  showBreadcrumb: false,
  enableClickableLogo: false,
};

const splitAddress = (address) => {
  const parts = String(address || '').split(',').map((part) => part.trim()).filter(Boolean);
  return { title: parts.shift() || 'Vị trí đã chọn', detail: parts.join(', ') };
};
const labelOf = (place) => place?.display || place?.address || '';

const RideMapPickerSheet = ({ initialPoint, target = 'PICKUP', onClose, onConfirm }) => {
  const [selected, setSelected] = useState(() => initialPoint
    ? [Number(initialPoint.latitude), Number(initialPoint.longitude)] : DEFAULT_CENTER);
  const [address, setAddress] = useState('');
  const [resolving, setResolving] = useState(true);
  const [mapMessage, setMapMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const requestRef = useRef(0);
  const keepResolvedAddressRef = useRef('');

  useEffect(() => {
    const preservedAddress = keepResolvedAddressRef.current;
    if (preservedAddress) {
      keepResolvedAddressRef.current = '';
      setAddress(preservedAddress);
      setResolving(false);
      setMapMessage('');
      return undefined;
    }
    let active = true;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setResolving(true);
    setMapMessage('');
    setAddress('');
    const timer = window.setTimeout(() => {
      customerRideApi.reverseGeocode({ lat: selected[0], lng: selected[1] })
        .then((response) => {
          if (!active || requestRef.current !== requestId) return;
          const place = response?.results?.[0] || response?.data?.results?.[0] || {};
          // VietMap's `address` contains only the administrative label for
          // many pin drops; `display` carries the actual nearest house/road.
          // The mobile RidePlaceSuggestion.fromVietmapReverse uses this exact
          // priority, so retain it on web as well.
          const resolved = place.display || place.address || '';
          if (!resolved) throw new Error('Address not found');
          setAddress(resolved);
          // The map result is shown above. Keep this field for the optional
          // house/road detail instead of pre-filling it with the same address.
        })
        .catch(() => {
          if (active && requestRef.current === requestId) setMapMessage('Không xác định được địa chỉ tại vị trí này. Chọn vị trí khác hoặc nhập địa chỉ chi tiết.');
        })
        .finally(() => { if (active && requestRef.current === requestId) setResolving(false); });
    }, RIDE_LOCATION_REVERSE_DEBOUNCE_MS);
    return () => { active = false; window.clearTimeout(timer); };
  }, [selected]);

  useEffect(() => {
    const text = searchQuery.trim();
    if (text.length < RIDE_LOCATION_MIN_QUERY_LENGTH || text === address) { setSuggestions([]); return undefined; }
    let active = true;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const response = await customerRideApi.autocomplete({
          text,
          lat: selected[0],
          lng: selected[1],
        });
        if (active) setSuggestions((response?.suggestions || []).slice(0, 6));
      } catch (_) {
        if (active) setSuggestions([]);
      } finally {
        if (active) setSearching(false);
      }
    }, RIDE_LOCATION_SEARCH_DEBOUNCE_MS);
    return () => { active = false; window.clearTimeout(timer); };
  }, [address, searchQuery, selected]);

  const moveTo = useCallback((latitude, longitude) => {
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) setSelected([latitude, longitude]);
  }, []);

  const onMapClick = useCallback((location) => moveTo(Number(location?.latitude), Number(location?.longitude)), [moveTo]);
  const chooseSuggestion = async (suggestion) => {
    if (!suggestion?.ref_id) return;
    setSearching(true);
    setMapMessage('');
    try {
      const response = await customerRideApi.resolvePlace(suggestion.ref_id);
      const place = response?.place || {};
      const latitude = Number(place.lat);
      const longitude = Number(place.lng);
      const exactAddress = labelOf(suggestion) || place.display || place.address;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !exactAddress) throw new Error('Invalid place');
      // Do not immediately replace the selected search address with a less
      // detailed nearest-road result from reverse geocoding.
      keepResolvedAddressRef.current = exactAddress;
      setSearchQuery(exactAddress);
      setSuggestions([]);
      moveTo(latitude, longitude);
    } catch (_) {
      setMapMessage('Không thể xác định địa chỉ chi tiết này. Vui lòng thử lại.');
    } finally {
      setSearching(false);
    }
  };
  const locateMe = () => {
    if (!navigator.geolocation) { setMapMessage('Trình duyệt này không hỗ trợ xác định vị trí.'); return; }
    setMapMessage('');
    navigator.geolocation.getCurrentPosition(
      (position) => moveTo(position.coords.latitude, position.coords.longitude),
      (error) => setMapMessage(error?.code === 1 ? 'Bạn chưa cho phép truy cập vị trí.' : 'Không thể lấy vị trí hiện tại. Vui lòng thử lại.'),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 },
    );
  };

  const preview = splitAddress(address);
  const canConfirm = !resolving && Boolean(address.trim());
  const pointLabel = target === 'PICKUP' ? 'Điểm đón' : 'Điểm đến';

  return (
    <RidePortal>
      <div className="ride-map-picker-backdrop" role="presentation" onMouseDown={onClose}>
        <section className="ride-map-picker" role="dialog" aria-modal="true" aria-label="Chọn vị trí trên bản đồ" onMouseDown={(event) => event.stopPropagation()}>
          <div className="ride-map-picker__head"><div><span>{pointLabel}</span><h3>Chọn trên bản đồ</h3><p>Chạm vào vị trí muốn chọn trên bản đồ.</p></div><button type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button></div>
          <div className="ride-map-picker__canvas">
            <ReactBingmaps key={`ride-picker-${selected.join('-')}`} id="ride-picker-bing-map" bingmapKey={process.env.REACT_APP_BING_MAP_API_KEY} center={selected} zoom={16} mapTypeId="road" mapOptions={mapOptions} getLocation={{ callback: onMapClick }} pushPins={[{ location: selected, option: { color: '#f04455' } }]} />
            <div className="ride-map-picker__search">
              <RideAppIcon name="search" />
              <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Tìm số nhà, tên đường hoặc địa điểm" />
              {searching && <RideAppIcon name="time" className="ride-icon-spin" />}
              {suggestions.length > 0 && <div className="ride-map-picker__suggestions">{suggestions.map((suggestion) => <button type="button" key={suggestion.ref_id} onClick={() => chooseSuggestion(suggestion)}><RideAppIcon name="destination" /><span>{labelOf(suggestion)}</span></button>)}</div>}
            </div>
            <button type="button" className="ride-map-picker__locate" onClick={locateMe}><RideAppIcon name="locate" /> Vị trí của tôi</button>
          </div>
          <div className="ride-map-picker__address"><RideAppIcon name={resolving ? 'time' : 'destination'} className={resolving ? 'ride-icon-spin' : ''} /><div>{resolving ? <><b>Đang xác định địa chỉ...</b><small>Đang tra cứu địa chỉ tại ghim trên bản đồ</small></> : <><b>{preview.title}</b>{preview.detail && <small>{preview.detail}</small>}</>}</div></div>
          {mapMessage && <p className="ride-map-picker__message">{mapMessage}</p>}
          <button type="button" className="ride-map-picker__confirm" disabled={!canConfirm} onClick={() => onConfirm({ address: address.trim(), latitude: selected[0], longitude: selected[1], provinceCode: null, communeCode: null })}>Xác nhận điểm này</button>
        </section>
      </div>
    </RidePortal>
  );
};

export default RideMapPickerSheet;
